import { scalar } from '@src/editor/services/utils';

import * as FadeLegacyParser from '../../serialization/FadeLegacyParser';
import { Element } from '../Element';
import { TranscriptSegment } from '../TranscriptSegment';

import type { TranscriptElement } from '../../../types.ts/transcript';
import type { HasTranscriptSegment } from './HasTranscriptSegment';

/**
 * VideoElement represents a video clip on the timeline with animation support
 * Aligned with Creatomate properties and structure
 *
 * Also implements HasTranscriptSegment to support associated transcript segments
 */
export class VideoElement extends Element implements HasTranscriptSegment {
  private _source: string; // Creatomate uses 'source' for media URL
  private _volume: string = '100%'; // Creatomate uses percentage strings
  private _fit: string = 'cover'; // How the video should be resized to fit the element (cover, contain, or fill)
  private _loop: boolean = false; // When true, the video starts over when it reaches the end
  private _audio_fade_in: number | null = null; // Duration in seconds for audio fade in
  private _audio_fade_out: number | null = null; // Duration in seconds for audio fade out
  private _media_duration: number | null = null; // Duration of the full video in seconds, if known
  private _reelSectionId: string | null = null; // PLAT-11050 — which reel Section this element belongs to

  // Transcript segment support
  transcriptSegment?: TranscriptSegment;
  originalTranscription: TranscriptElement[] = [];

  constructor(props: Partial<VideoElement> = {}) {
    super({ ...props, type: 'video' });

    // Support both source and src properties for backwards compatibility
    this._source = props.source ?? (props as any).src ?? '';

    // Handle volume as a percentage string (Creatomate format)
    if (props.volume !== undefined) {
      if (typeof props.volume === 'number') {
        // Convert number (0-1) to percentage string
        const percentage = Math.round(props.volume * 100);
        this._volume = `${percentage}%`;
      } else if (typeof props.volume === 'string') {
        this._volume = props.volume;
      }
    } else if ((props as any).isMuted) {
      this._volume = '0%';
    }

    // Create a copy of props with trim_duration set to null if it's Infinity
    const adjustedProps = { ...props };

    // Override trim_duration if it's set to Infinity (to avoid API errors)
    if (adjustedProps.trim_duration === Infinity) {
      adjustedProps.trim_duration = null;
    }

    this._fit = adjustedProps.fit ?? 'cover';
    this._loop = adjustedProps.loop ?? false;
    this._audio_fade_in = adjustedProps.audio_fade_in ?? null;
    this._audio_fade_out = adjustedProps.audio_fade_out ?? null;
    this._media_duration = adjustedProps.media_duration ?? null;
    this._reelSectionId = adjustedProps.reelSectionId ?? null;

    // Initialize transcript properties
    if (props.transcriptSegment !== undefined) {
      this.transcriptSegment = props.transcriptSegment;
    }
    if (props.originalTranscription !== undefined) {
      this.originalTranscription = [...props.originalTranscription];
    }
  }

  // Property getters
  get source(): string {
    return this._source;
  }

  get volume(): string {
    return this._volume;
  }

  get fit(): string {
    return this._fit;
  }

  get loop(): boolean {
    return this._loop;
  }

  get audio_fade_in(): number | null {
    return this._audio_fade_in;
  }

  get audio_fade_out(): number | null {
    return this._audio_fade_out;
  }

  get media_duration(): number | null {
    return this._media_duration;
  }

  get reelSectionId(): string | null {
    return this._reelSectionId;
  }

  // Property setters
  set source(value: string) {
    this._source = value;
  }

  set volume(value: string) {
    this._volume = value;
  }

  set fit(value: string) {
    this._fit = value;
  }

  set loop(value: boolean) {
    this._loop = value;
  }

  set audio_fade_in(value: number | null) {
    this._audio_fade_in = value;
  }

  set audio_fade_out(value: number | null) {
    this._audio_fade_out = value;
  }

  set media_duration(value: number | null) {
    this._media_duration = value;
  }

  set reelSectionId(value: string | null) {
    this._reelSectionId = value;
  }

  /**
   * Updates video element properties
   */
  override update(props: Partial<VideoElement>): void {
    super.update(props);

    if (props.source !== undefined) {
      this._source = props.source;
    }
    if (props.volume !== undefined) {
      if (typeof props.volume === 'number') {
        const percentage = Math.round(props.volume * 100);
        this._volume = `${percentage}%`;
      } else {
        this._volume = props.volume;
      }
    }
    if (props.fit !== undefined) this._fit = props.fit;
    if (props.loop !== undefined) this._loop = props.loop;
    if (props.audio_fade_in !== undefined)
      this._audio_fade_in = props.audio_fade_in;
    if (props.audio_fade_out !== undefined)
      this._audio_fade_out = props.audio_fade_out;
    if (props.reelSectionId !== undefined)
      this._reelSectionId = props.reelSectionId;

    // Update transcript properties
    if (props.transcriptSegment !== undefined) {
      this.transcriptSegment = props.transcriptSegment;
    }
    if (props.originalTranscription !== undefined) {
      this.originalTranscription = [...props.originalTranscription];
    }
  }

  /**
   * Captures the mutable state of the video element.
   */
  override getState(): Record<string, any> {
    return {
      ...super.getState(),
      source: this._source,
      volume: this._volume,
      fit: this._fit,
      loop: this._loop,
      audio_fade_in: this._audio_fade_in,
      audio_fade_out: this._audio_fade_out,
      media_duration: this._media_duration,
      // PLAT-11050 — carried here so reel Section provenance survives every path
      // that rebuilds an element from state: the reel crop, transcript
      // re-projection, track rebuild, clone, trim and split.
      reelSectionId: this._reelSectionId,
      transcriptSegment: this.transcriptSegment,
      originalTranscription: [...this.originalTranscription],
    };
  }

  /**
   * Restores the video element's mutable state.
   */
  override restoreState(state: Record<string, any>): void {
    super.restoreState(state);
    if (state.source !== undefined) this._source = state.source;
    if (state.volume !== undefined) this._volume = state.volume;
    if (state.fit !== undefined) this._fit = state.fit;
    if (state.loop !== undefined) this._loop = state.loop;
    if (state.audio_fade_in !== undefined)
      this._audio_fade_in = state.audio_fade_in;
    if (state.audio_fade_out !== undefined)
      this._audio_fade_out = state.audio_fade_out;
    if (state.media_duration !== undefined)
      this._media_duration = state.media_duration;
    if (state.reelSectionId !== undefined)
      this._reelSectionId = state.reelSectionId;
    if (state.transcriptSegment !== undefined)
      this.transcriptSegment = state.transcriptSegment;
    if (state.originalTranscription !== undefined) {
      this.originalTranscription = [...state.originalTranscription];
    }
  }

  /**
   * Creates a deep clone of this video element
   */
  override clone(): VideoElement {
    const el = new VideoElement({
      ...this.getState(),
      source: this._source,
      volume: this._volume,
      fit: this._fit,
      loop: this._loop,
      audio_fade_in: this._audio_fade_in,
      audio_fade_out: this._audio_fade_out,
      media_duration: this._media_duration,
    });

    // Attach the original transcription to the cloned element
    el.setOriginalTranscription(this.originalTranscription);

    return el;
  }

  protected override getAdditionalProperties(): Record<string, any> {
    // Only include properties in the JSON if they have non-default values
    const props: Record<string, any> = {
      source: this._source,
      volume: this._volume,
    };

    // Only include trim_start if it's not null, and format it as "X s"
    if (this.trim_start !== null) {
      props.trim_start = this.formatTimeProperty(this.trim_start);
    }

    // Only include trim_duration if it's not null, and format it as "X s"
    if (this.trim_duration !== null) {
      props.trim_duration = this.formatTimeProperty(this.trim_duration);
    }

    // Only include non-default values to reduce JSON clutter
    if (this._fit !== 'cover') {
      props.fit = this._fit;
    }

    if (this._loop !== false) {
      props.loop = this._loop;
    }

    // Audio fade properties - only include if they have values
    if (this._audio_fade_in !== null) {
      props.audio_fade_in = this.formatTimeProperty(this._audio_fade_in);
    }

    if (this._audio_fade_out !== null) {
      props.audio_fade_out = this.formatTimeProperty(this._audio_fade_out);
    }

    if (this._media_duration !== null) {
      props.media_duration = this.formatTimeProperty(this._media_duration);
    }

    // PLAT-11050 — reel Section provenance; absent on elements never cropped
    // into a reel, so ordinary clips gain no JSON clutter.
    if (this._reelSectionId !== null) {
      props.reelSectionId = this._reelSectionId;
    }

    return props;
  }

  // HasTranscriptSegment implementation
  getMediaStartTime(): number {
    // For VideoElement, media start time is the trim_start if present, otherwise 0
    return this.trim_start ?? 0;
  }

  getMediaEndTime(): number {
    // For VideoElement, media end time is trim_start + trim_duration if both present,
    // otherwise trim_start + duration, otherwise just duration
    const startTime = this.trim_start ?? 0;

    if (this.trim_duration !== null) {
      return startTime + this.trim_duration;
    }

    if (typeof this.duration === 'number') {
      return startTime + this.duration;
    }

    // If duration is not a number, fallback to just start time
    return startTime;
  }

  getTranscriptSegment(): TranscriptSegment | undefined {
    // Compute the transcript segment based on current media times if we don't have one cached
    if (!this.transcriptSegment && this.originalTranscription.length > 0) {
      this.transcriptSegment = new TranscriptSegment(
        this.originalTranscription,
        this.getMediaStartTime(),
        this.getMediaEndTime(),
      );
    }
    return this.transcriptSegment;
  }

  getActiveTranscriptText(): string {
    const segment = this.getTranscriptSegment();
    if (!segment) {
      return '';
    }
    return segment.getText();
  }

  setTranscriptSegment(segment: TranscriptSegment | undefined): void {
    this.transcriptSegment = segment;
  }

  hasTranscriptSegment(): boolean {
    return this.transcriptSegment !== undefined;
  }

  getOriginalTranscription(): TranscriptElement[] {
    return [...this.originalTranscription];
  }

  setOriginalTranscription(transcription: TranscriptElement[]): void {
    this.originalTranscription = [...transcription];
    // Clear the cached segment so it will be recomputed on next access
    this.transcriptSegment = undefined;
  }

  /**
   * Invalidates the cached transcript segment, forcing it to be recomputed
   * Used when element timing changes (trim, duration, etc.)
   */
  invalidateTranscriptSegment(): void {
    this.transcriptSegment = undefined;
  }

  /**
   * Creates a VideoElement from JSON data
   */
  static fromJSON(json: Record<string, any>): VideoElement {
    // Parse time-based properties that Element would handle
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);
    const parsedTrimStart = Element.parseTimeProperty(json.trim_start);
    const parsedTrimDuration = Element.parseTimeProperty(json.trim_duration);
    const parsedAudioFadeIn = Element.parseTimeProperty(json.audio_fade_in);
    const parsedAudioFadeOut = Element.parseTimeProperty(json.audio_fade_out);

    const parsedMediaDuration = Element.parseTimeProperty(json.media_duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    // Parse legacy fade overlays into native animation types
    let animations = Array.isArray(json.animations) ? json.animations : [];
    let colorOverlay = json.color_overlay ?? null;
    const legacyFade = FadeLegacyParser.parseFromOverlays(colorOverlay);
    if (legacyFade) {
      animations = [
        ...animations.filter(
          (a: any) => a.type !== 'dip' && a.type !== 'crossfade',
        ),
        ...FadeLegacyParser.toNativeAnimations(legacyFade),
      ];
      colorOverlay = null;
    }
    // Strip orphaned crossfade overlays from original video track elements
    if (
      !legacyFade &&
      FadeLegacyParser.hasLegacyCrossfadeOverlays(colorOverlay)
    ) {
      colorOverlay =
        FadeLegacyParser.stripLegacyCrossfadeOverlays(colorOverlay);
    }

    return new VideoElement({
      // Base element properties
      id: json.id,
      name: json.name ?? null,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      trim_start: typeof parsedTrimStart === 'number' ? parsedTrimStart : null,
      trim_duration:
        typeof parsedTrimDuration === 'number' ? parsedTrimDuration : null,
      type: 'video', // Force video type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: scalar(json.x, '50%'),
      y: scalar(json.y, '50%'),
      width: scalar(json.width, '100%'),
      height: scalar(json.height, '100%'),

      // Transform properties (now handled by Element)
      aspect_ratio: json.aspect_ratio ?? null,
      x_padding: scalar(json.x_padding, '0 vw'),
      y_padding: scalar(json.y_padding, '0 vh'),
      z_index: json.z_index ?? null,
      x_anchor: scalar(json.x_anchor, '50%'),
      y_anchor: scalar(json.y_anchor, '50%'),
      x_scale: scalar(json.x_scale, '100%'),
      y_scale: scalar(json.y_scale, '100%'),
      x_skew: scalar(json.x_skew, '0'),
      y_skew: scalar(json.y_skew, '0'),
      x_rotation: scalar(json.x_rotation, '0'),
      y_rotation: scalar(json.y_rotation, '0'),
      z_rotation: scalar(json.z_rotation, '0'),
      perspective: json.perspective ?? null,
      backface_visible: json.backface_visible ?? true,
      border_radius: json.border_radius ?? '0 vmin',

      // Alignment properties (now handled by Element)
      x_alignment: json.x_alignment ?? '50%',
      y_alignment: json.y_alignment ?? '50%',

      // Visual effects (now handled by Element)
      opacity: json.opacity ?? '100%',
      blend_mode: json.blend_mode ?? 'none',
      clip: json.clip ?? false,

      // Animation support (now handled by Element)
      animations,
      color_overlay: colorOverlay,

      // Chroma Key properties (now handled by Element)
      chroma_key_color: json.chroma_key_color ?? null,
      chroma_key_sensitivity: json.chroma_key_sensitivity ?? null,

      // Video-specific properties
      source: json.source ?? json.src ?? '',
      volume: json.volume ?? '100%',
      fit: json.fit ?? 'cover',
      loop: json.loop ?? false,
      audio_fade_in:
        typeof parsedAudioFadeIn === 'number' ? parsedAudioFadeIn : null,
      audio_fade_out:
        typeof parsedAudioFadeOut === 'number' ? parsedAudioFadeOut : null,
      media_duration:
        typeof parsedMediaDuration === 'number' ? parsedMediaDuration : null,

      // PLAT-11050 — reel Section provenance
      reelSectionId:
        typeof json.reelSectionId === 'string' ? json.reelSectionId : null,
    });
  }
}

const CONTINUITY_TOLERANCE = 1e-4;

/**
 * Two video elements are "continuous" when `b` plays seamlessly after `a`:
 * same source, time-adjacent on the timeline, and trim-contiguous in the
 * underlying media. Mirrors the logic in
 * `LayoutEngine.mergeContiguousVideoChildren`.
 */
export const areVideoElementsContinuous = (
  a: VideoElement,
  b: VideoElement,
): boolean => {
  if (a.source !== b.source) {
    return false;
  }

  const timeAdjacent =
    Math.abs(b.time - (a.time + a.durationSeconds)) < CONTINUITY_TOLERANCE;
  if (!timeAdjacent) {
    return false;
  }

  const aTrimStart = a.trim_start ?? 0;
  const bTrimStart = b.trim_start ?? 0;
  const trimContiguous =
    Math.abs(bTrimStart - (aTrimStart + a.durationSeconds)) <
    CONTINUITY_TOLERANCE;

  return trimContiguous;
};
